import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { Product } from '@/app/shared/interface/product.interface';
import { AuthService } from '@/app/shared/services/auth.service';
import { SearchSuggestionService } from '@/app/shared/services/search-suggestion.service';
import { TrackingService } from '@/app/shared/services/tracking.service';
import { SuggestionView, toSuggestionView } from './search-suggestion-view';

/**
 * Phần gợi ý trên trang kết quả tìm kiếm, đặt TRÊN kết quả tìm kiếm thường:
 * - "Có phải bạn muốn tìm: …?" (bảng map từ khóa, kèm nút Xem tất cả);
 * - "GiaThuocTot gợi ý" (sản phẩm thay thế cho hàng không bán).
 * Dùng đúng `app-product-box` như danh sách kết quả. Chưa có phản hồi, rỗng, lỗi hoặc khách chưa đăng nhập thì KHÔNG hiển thị gì;
 * phản hồi về sau thì chèn vào và đẩy kết quả bên dưới xuống. Chỉ ở trang đầu của kết quả.
 * Thiết kế: docs/guides/dac-ta-search-map.md (repo DvEventSourcing).
 */
@Component({
  selector: 'app-search-suggestions',
  templateUrl: './search-suggestions.component.html',
  styleUrl: './search-suggestions.component.scss'
})
export class SearchSuggestionsComponent implements OnInit {
  @Input() isListView: boolean = false;
  /** true khi phần gợi ý đang hiển thị: trang dùng để hiện tiêu đề cho kết quả thường và ẩn thông báo "không tìm thấy". */
  @Output() visibleChange = new EventEmitter<boolean>();
  /** true từ lúc từ khóa đổi (khi sẽ gọi API gợi ý) tới lúc có phản hồi: trang dùng để hiện skeleton thay vì "Không tìm thấy" trong lúc chờ gợi ý. */
  @Output() pendingChange = new EventEmitter<boolean>();

  /** Gợi ý ra sau khi từ khóa yên lặng chừng này: tìm kiếm cập nhật URL theo từng lần ngừng gõ, không gọi theo từng phím. */
  static readonly DEBOUNCE_MS = 250;

  public view$: Observable<SuggestionView | null> = of(null);

  constructor(
    private route: ActivatedRoute,
    private suggestions: SearchSuggestionService,
    private auth: AuthService,
    private tracking: TrackingService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    // Server (SSR) không gọi: gợi ý chỉ có ý nghĩa với khách đã đăng nhập, mà trang tìm kiếm không SSR kết quả
    if (!isPlatformBrowser(this.platformId)) return;

    this.view$ = this.route.queryParamMap.pipe(
      map((params) => ({
        term: (params.get('searchTerm') ?? params.get('s') ?? '').trim(),
        page: Number(params.get('page') ?? 1) || 1,
      })),
      distinctUntilChanged((a, b) => a.term === b.term && a.page === b.page),
      tap(({ term, page }) => this.setPending(this.willFetch(term, page))),
      debounceTime(SearchSuggestionsComponent.DEBOUNCE_MS),
      switchMap(({ term, page }) => {
        if (!this.willFetch(term, page)) return of(null);
        const startedAt = Date.now();
        return this.suggestions.get(term).pipe(
          map((res) => toSuggestionView(term, res, startedAt, this.tracking)),
          tap(() => this.setPending(false)),
          // Từ khóa đổi thì gợi ý cũ biến mất ngay, không nằm lại dưới kết quả của từ khóa mới
          startWith(null),
        );
      }),
      tap((view) => this.visibleChange.emit(!!view)),
    );
  }

  private willFetch(term: string, page: number): boolean {
    return !!term && page <= 1 && this.auth.isAuthenticated();
  }

  /** Phát ở microtask: ngOnInit chạy trong lúc trang cha đang kiểm tra thay đổi, phát đồng bộ sẽ gây ExpressionChanged. */
  private setPending(pending: boolean) {
    queueMicrotask(() => this.pendingChange.emit(pending));
  }

  trackByProduct(_: number, product: Product): unknown {
    return product.id ?? product.sku;
  }
}
